import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress } from '@munifocus/ui';
import type { OperationsStatus } from '@munifocus/contracts-gov';
import { useOptionalScope } from '../../providers/ScopeProvider';

type Municipality = OperationsStatus['municipalities'][number];

/**
 * The municipality an operations screen works on: the active scope, when it is
 * a town where the module is on and the account can do this there. Null with
 * a reason otherwise, which the screen shows instead of an empty board.
 */
export function useOpsMunicipality(can: (m: Municipality) => boolean): {
  municipality: Municipality | null;
  status: OperationsStatus | null;
  reason: 'loading' | 'pickMunicipality' | 'notEnabled' | null;
} {
  const scope = useOptionalScope();
  const status = scope?.operations ?? null;
  const active = scope?.active ?? null;
  if (!status) return { municipality: null, status, reason: 'loading' };
  if (active?.kind !== 'local_government') {
    return { municipality: null, status, reason: 'pickMunicipality' };
  }
  const found = status.municipalities.find((m) => m.localGovernmentId === active.id);
  if (!found || !can(found)) return { municipality: null, status, reason: 'notEnabled' };
  return { municipality: found, status, reason: null };
}

/** What an operations screen shows when it has no municipality to work on. */
export function OpsScopeNotice({
  reason,
}: {
  reason: 'loading' | 'pickMunicipality' | 'notEnabled';
}) {
  const { t } = useTranslation();
  if (reason === 'loading') return <Loading />;
  return (
    <Alert severity="info">
      {t(reason === 'pickMunicipality' ? 'gov.ops.pickMunicipality' : 'gov.ops.notEnabled')}
    </Alert>
  );
}

export function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

export function LoadError({ onRetry, children }: { onRetry: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          {t('common.actions.retry')}
        </Button>
      }
    >
      {children}
    </Alert>
  );
}
